import { FC } from 'react';
import Close from '../../assets/close.png';
import BackArrow from '../../assets/back_arrow.png';
import AgreementButton from '../../component/button/button';
import { AgreementModalProps } from './agreementModal.type';
import { Trans } from 'react-i18next';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import ContinueButton from '../continueButton/continueButton';
import './agreementModal.css';
import ButtonSpinner from '../../assets/buttonLoading/buttonLoading';
import Loading from '../../assets/loading/loading';
import { getIdAttributes } from '../../utils/helper';

const AgreementModal: FC<AgreementModalProps> = (props) => {
  const { isDesktop } = useResponsiveLayout();
  const now = new Date();
  const currentMonthYear = now.toLocaleString('default', {
    month: 'short',
    year: 'numeric',
  });

  const idPrefix: string = 'terms-modal';

  return (
    <div
      className="agree-modal-container"
      {...getIdAttributes(idPrefix, 'container')}
    >
      <div className="agree-modal-inner-container">
        <div
          className="header-container relative"
          {...getIdAttributes(idPrefix, 'agreement-header')}
        >
          {!props.isView &&
            (props.isBackLoading ? (
              <div
                className="w-4 h-4 flex items-center justify-center"
                {...getIdAttributes(idPrefix, 'agreement-back-loading')}
              >
                <Loading height={30} width={30} />
              </div>
            ) : (
              <img
                onClick={props.handleBack}
                src={BackArrow}
                alt="Back Arrow"
                className="w-4 h-4 cursor-pointer"
                {...getIdAttributes(idPrefix, 'agreement-back-button')}
              />
            ))}

          {isDesktop && (
            <div
              className="agreement-button-container"
              {...getIdAttributes(idPrefix, 'agreement-button-container')}
            >
              <AgreementButton
                className="w-52 py-3 rounded-full text-sm"
                {...getIdAttributes(idPrefix, 'agreement-button')}
              >
                <Trans i18nKey={props.agreementTitle} />
              </AgreementButton>
            </div>
          )}

          <div
            className="ml-auto flex items-center gap-4"
            {...getIdAttributes(idPrefix, 'agreement-close-container')}
          >
            {props.isCloseLoading ? (
              <div
                className="w-4 h-4 flex items-center justify-center"
                {...getIdAttributes(idPrefix, 'agreement-close-loading')}
              >
                <Loading height={30} width={30} />
              </div>
            ) : (
              <img
                onClick={props.handleOnClose}
                src={Close}
                alt="Close"
                className="w-4 h-4 cursor-pointer"
                {...getIdAttributes(idPrefix, 'agreement-close-button')}
              />
            )}
          </div>
        </div>

        <div
          className="text-center px-6"
          {...getIdAttributes(idPrefix, 'text-container')}
        >
          <p
            className="lg:text-3xl md:text-2xl text-[#000000]"
            {...getIdAttributes(idPrefix, 'agreement-modal-title')}
          >
            <Trans i18nKey={props.agreementSubTitle} />
          </p>
          <p
            className="text-sm text-[#707070] mt-2"
            {...getIdAttributes(idPrefix, 'agreement-version')}
          >
            <Trans i18nKey="agreement.versionUpdated" /> {currentMonthYear}
          </p>
        </div>

        <div
          className={`overflow-y-auto px-14 md:mt-5 lg:mt-10 text-sm ${
            props.isView ? 'h-[620px]' : 'h-[540px]'
          }`}
          {...getIdAttributes(idPrefix, 'agreement-modal-content')}
        >
          <Trans
            i18nKey={props.content}
            values={{ currentDate: new Date().toLocaleDateString('en-GB') }}
            components={{
              strong: <strong />,
              div: <div />,
              span: <span />,
              a: <a href="#" target="" rel="noopener noreferrer" />,
            }}
          />
        </div>

        {!props.isView && (
          <div
            className="px-14 py-4 md:pb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-10"
            {...getIdAttributes(idPrefix, 'agreement-footer')}
          >
            <label
              className="flex items-center text-sm"
              {...getIdAttributes(idPrefix, 'agreement-checkbox-label')}
            >
              <input
                type="checkbox"
                checked={props.agreeAccepted}
                onChange={() => props.setAgreeAccepted(!props.agreeAccepted)}
                className="mr-2 w-5 h-5 bg-black"
                {...getIdAttributes(idPrefix, 'agreement-checkbox')}
              />
              <Trans i18nKey={props.agreementText} />
            </label>

            <ContinueButton
              accepted={props.agreeAccepted}
              onClick={props.handleContinue}
              {...getIdAttributes(idPrefix, 'agreement-continue-button')}
            >
              {props.isLoading ? (
                <ButtonSpinner
                  loadingColor="white"
                  {...getIdAttributes(idPrefix, 'agreement-button-spinner')}
                />
              ) : (
                <Trans i18nKey="agreement.continue" />
              )}
            </ContinueButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgreementModal;
